import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from './useAccessToken';
import { useMemo, useCallback } from 'react';
import {
  EdiAdminClientUsersService,
  OAuthClientUser,
} from '../services/EdiAdminClientUsersService';
import { useLogger } from '../context/useLogger';
import { Collaborator } from '../types/collaborator.types';

const COLLABORATORS_QUERY_KEY = ['collaborators'] as const;

const mapCollaboratorToUser = (user: Collaborator): OAuthClientUser => ({
  InstanceId: user.instanceId,
  FirstName: user.firstName ?? '',
  LastName: user.lastName ?? '',
  Email: user.email ?? '',
  Actif: user.isActive ?? false,
  ProfileId: user.profileId ?? '',
  Profile: user.profileLabel ?? undefined,
});

const mapUserToCollaborator = (user: OAuthClientUser): Collaborator => ({
  instanceId: user.InstanceId,
  firstName: user.FirstName ?? '',
  lastName: user.LastName ?? '',
  email: user.Email ?? '',
  isActive: user.Actif ?? false,
  profileId: user.ProfileId ?? '',
  profileLabel: user.Profile ?? null,
});

export const useCollaborators = () => {
  const getAccessToken = useAccessToken();
  const logger = useLogger();
  const queryClient = useQueryClient();

  const backendService = useMemo(
    () => new EdiAdminClientUsersService(getAccessToken, logger),
    [getAccessToken, logger],
  );

  // Query pour récupérer les collaborateurs
  const {
    data: collaborators = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: COLLABORATORS_QUERY_KEY,
    queryFn: async () => {
      const users = await backendService.fetchGetClientUsers();
      return users.map(mapUserToCollaborator);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Mutation pour ajouter un collaborateur
  const addCollaboratorMutation = useMutation({
    mutationFn: async ({
      newUser,
      confirmEmail,
    }: {
      newUser: Collaborator;
      confirmEmail: string;
    }) => {
      if (!newUser.email || newUser.email !== confirmEmail) {
        throw new Error('Les emails ne correspondent pas ou sont vides');
      }

      const mappedUser = mapCollaboratorToUser(newUser);
      const createdUser = await backendService.fetchCreateClientUsers(mappedUser);

      if (!createdUser) {
        throw new Error("Le collaborateur créé n'est pas valide");
      }

      return createdUser;
    },
    onSuccess: () => {
      // Invalide le cache pour recharger automatiquement
      void queryClient.invalidateQueries({ queryKey: COLLABORATORS_QUERY_KEY });
    },
    onError: error => {
      logger.error('Error adding collaborator:', error);
    },
  });

  // Mutation pour modifier un collaborateur
  const editCollaboratorMutation = useMutation({
    mutationFn: async (updatedUser: Collaborator) => {
      const mappedUser = mapCollaboratorToUser(updatedUser);
      const user = await backendService.fetchUpdateClientUsers(mappedUser);

      if (!user) {
        throw new Error("Le collaborateur mis à jour n'est pas valide");
      }

      return user;
    },
    onSuccess: () => {
      // Invalide le cache pour recharger automatiquement
      void queryClient.invalidateQueries({ queryKey: COLLABORATORS_QUERY_KEY });
    },
    onError: error => {
      logger.error('Error updating collaborator:', error);
    },
  });

  // Mutation pour supprimer un collaborateur
  const deleteCollaboratorMutation = useMutation({
    mutationFn: async (id: string) => {
      const user = await backendService.fetchResetClientUser(id);
      if (!user) {
        throw new Error('Echec de la suppression du collaborateur');
      }
      return user;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COLLABORATORS_QUERY_KEY });
    },
    onError: error => {
      logger.error('Error deleting collaborator:', error);
    },
  });

  // Wrapper functions
  const addCollaborator = useCallback(
    async (
      newUser: Collaborator,
      confirmEmail: string,
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        await addCollaboratorMutation.mutateAsync({ newUser, confirmEmail });
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error:
            err instanceof Error
              ? err.message
              : 'Une erreur est survenue lors de la création du collaborateur',
        };
      }
    },
    [addCollaboratorMutation],
  );

  const editCollaborator = useCallback(
    async (updatedUser: Collaborator): Promise<{ success: boolean; error?: string }> => {
      try {
        await editCollaboratorMutation.mutateAsync(updatedUser);
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error:
            err instanceof Error
              ? err.message
              : 'Une erreur est survenue lors de la mise à jour du collaborateur',
        };
      }
    },
    [editCollaboratorMutation],
  );

  const deleteCollaborator = useCallback(
    async (id: string): Promise<{ success: boolean; error?: string }> => {
      try {
        await deleteCollaboratorMutation.mutateAsync(id);
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error:
            err instanceof Error
              ? err.message
              : 'Une erreur est survenue lors de la suppression du collaborateur',
        };
      }
    },
    [deleteCollaboratorMutation],
  );

  return {
    collaborators,
    isLoading,
    error: error instanceof Error ? error.message : null,
    addCollaborator,
    editCollaborator,
    deleteCollaborator,
    refetchCollaborators: refetch,
    // Exposer les mutations si besoin pour des cas avancés
    isAdding: addCollaboratorMutation.isPending,
    isEditing: editCollaboratorMutation.isPending,
    isDeleting: deleteCollaboratorMutation.isPending,
  };
};
